import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  FiFileText, FiX,
  FiChevronRight, FiChevronDown,
  FiArrowLeft, FiArrowRight,
} from 'react-icons/fi';
import { AmsFileItem, AmsFileSelection } from '../commissionscan/CommissionScanClientFiles';
import '../commissionscan/CommissionScan.css';
const getToolboxToken = () => localStorage.getItem('toolboxToken') || '';

interface Props {
  insuredId: string;
  clientName?: string;
  onFileSelected: (sel: AmsFileSelection | null) => void;
}

const DriverLicenseScanAmsFiles: React.FC<Props> = ({ insuredId, clientName, onFileSelected }) => {
  const [rootFolder,      setRootFolder]      = useState<AmsFileItem | null>(null);
  const [currentFolder,   setCurrentFolder]   = useState<AmsFileItem | null>(null);
  const [tableData,       setTableData]       = useState<AmsFileItem[]>([]);
  const [tableLoading,    setTableLoading]    = useState(false);
  const [fetchError,      setFetchError]      = useState('');
  const [expandedFolders, setExpandedFolders] = useState<Record<string, boolean>>({});
  const [selectedFile,    setSelectedFile]    = useState<AmsFileItem | null>(null);
  const [folderHistory,   setFolderHistory]   = useState<AmsFileItem[]>([]);
  const [historyIndex,    setHistoryIndex]    = useState(-1);
  const prevInsuredIdRef = useRef<string>('');

  const fetchFiles = useCallback(async (folderId?: string) => {
    const url = `${(process.env.REACT_APP_NOWCERTSAPI || '')}/api/nowcert/getInsuredFilesList?insuredId=${insuredId}`
      + `&nctoken=${localStorage.getItem('amsToken') || ''}`
      + `&folderId=${folderId || ''}`;
    const res = await axios.get(url, { headers: { Authorization: `Bearer ${getToolboxToken()}` } });
    return res.data;
  }, [insuredId]);

  const mapFiles = (rawFiles: any[], parentId: string): AmsFileItem[] =>
    (rawFiles || []).map((f: any) => ({
      id:            f.databaseId,
      name:          f.name,
      type:          f.fileOrFolder === 'Folder' ? 'folder' : 'file',
      description:   f.fileOrFolder,
      size:          f.size?.toString() || '-',
      uploader:      f.creatorName || '-',
      created:       f.createDate  || '-',
      lastModified:  f.changeDate  || '-',
      downloadUrl:   f.downloadUrl,
      children:      f.fileOrFolder === 'Folder' ? [] : undefined,
      parentFolderId: f.parentId || parentId,
    }));

  useEffect(() => {
    if (!insuredId) return;
    const idChanged = prevInsuredIdRef.current !== insuredId;
    prevInsuredIdRef.current = insuredId;
    if (!idChanged) return;
    setFetchError('');
    setRootFolder(null); setCurrentFolder(null);
    setTableData([]); setExpandedFolders({});
    setFolderHistory([]); setHistoryIndex(-1);
    setSelectedFile(null); onFileSelected(null);

    setTableLoading(true);
    fetchFiles()
      .then(data => {
        const fd = data?.data || data;
        const root: AmsFileItem = {
          id:       fd.currentFolderId || insuredId,
          name:     fd.name || clientName || 'Root',
          type:     'folder',
          children: mapFiles(fd.files || [], fd.currentFolderId || insuredId),
        };
        setRootFolder(root);
        setCurrentFolder(root);
        setTableData(root.children || []);
        setExpandedFolders({ [root.id]: true });
        setFolderHistory([root]);
        setHistoryIndex(0);
        setTableLoading(false);
      })
      .catch(() => {
        setFetchError('Unable to load AMS files. Please try again.');
        setTableLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insuredId]);

  const openFolder = useCallback((folder: AmsFileItem, childrenOverride?: AmsFileItem[]) => {
    const children = childrenOverride ?? folder.children ?? [];
    const node: AmsFileItem = { ...folder, children };
    setCurrentFolder(node);
    setTableData(children);
    setExpandedFolders(p => ({ ...p, [folder.id]: true }));
    setFolderHistory(prev => {
      const base = historyIndex >= 0 ? prev.slice(0, historyIndex + 1) : [];
      const exists = base.findIndex(f => f.id === folder.id);
      if (exists !== -1) { setHistoryIndex(exists); return base; }
      const next = [...base, node];
      setHistoryIndex(next.length - 1);
      return next;
    });
  }, [historyIndex]);

  const updateChildrenInTree = (root: AmsFileItem, targetId: string, newChildren: AmsFileItem[]): AmsFileItem => {
    if (root.id === targetId) return { ...root, children: newChildren };
    if (root.children) return { ...root, children: root.children.map(c => updateChildrenInTree(c, targetId, newChildren)) };
    return root;
  };

  const toggleFolder = useCallback((item: AmsFileItem) => {
    if (expandedFolders[item.id]) {
      setExpandedFolders(p => ({ ...p, [item.id]: false }));
      return;
    }
    if (item.type === 'folder') {
      if (item.children && item.children.length > 0) {
        openFolder(item);
        return;
      }
      setTableLoading(true);
      fetchFiles(item.id)
        .then(data => {
          const fd = data?.data || data;
          const newChildren = mapFiles(fd.files || [], item.id);
          setRootFolder(prev => prev ? updateChildrenInTree(prev, item.id, newChildren) : prev);
          openFolder(item, newChildren);
          setTableLoading(false);
        })
        .catch(() => setTableLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedFolders, fetchFiles, openFolder]);

  const canGoBack    = historyIndex > 0;
  const canGoForward = historyIndex >= 0 && historyIndex < folderHistory.length - 1;

  const handleBack = () => {
    const f = folderHistory[historyIndex - 1];
    if (!f) return;
    setHistoryIndex(i => i - 1); setCurrentFolder(f);
    setTableData(f.children || []);
    setExpandedFolders(p => ({ ...p, [f.id]: true }));
  };

  const handleForward = () => {
    const f = folderHistory[historyIndex + 1];
    if (!f) return;
    setHistoryIndex(i => i + 1); setCurrentFolder(f);
    setTableData(f.children || []);
    setExpandedFolders(p => ({ ...p, [f.id]: true }));
  };

  const pathSegments = useMemo(() => {
    if (!rootFolder) return [] as AmsFileItem[];
    const target = currentFolder?.id || rootFolder.id;
    const path: AmsFileItem[] = [];
    const dfs = (node: AmsFileItem, acc: AmsFileItem[]): boolean => {
      const next = [...acc, node];
      if (node.id === target) { path.push(...next); return true; }
      for (const c of node.children || []) if (dfs(c, next)) return true;
      return false;
    };
    dfs(rootFolder, []);
    return path.length ? path : [rootFolder];
  }, [rootFolder, currentFolder]);

  const fmtSize = (size?: string) => {
    if (!size || size === '-') return '—';
    const n = parseInt(size, 10);
    if (isNaN(n)) return size;
    return n >= 1048576 ? (n / 1048576).toFixed(2) + ' MB' : (n / 1024).toFixed(2) + ' KB';
  };

  const renderTreeItem = (item: AmsFileItem, level = 0): React.ReactNode => {
    const expanded = expandedFolders[item.id];
    return (
      <div key={item.id}>
        <div
          className={`cs-ams-tree-row${level === 0 ? ' cs-ams-tree-row--root' : ''}`}
          style={{ paddingLeft: level * 16 + 10 }}
          onClick={() => toggleFolder(item)}
        >
          {expanded
            ? <FiChevronDown  size={11} className="cs-ams-chevron" />
            : <FiChevronRight size={11} className="cs-ams-chevron" />}
          <span className="cs-ams-tree-name" title={item.name}>{item.name}</span>
        </div>
        {expanded && (item.children || [])
          .filter(c => c.type === 'folder')
          .map(c => renderTreeItem(c, level + 1))}
      </div>
    );
  };

  return (
    <div className="cs-ams-wrap">
      {tableLoading && !rootFolder && (
        <div className="cs-ams-state">
          <span className="cs-ams-spinner" />
          <span>Loading files…</span>
        </div>
      )}

      {fetchError && <div className="cs-ams-error-msg">{fetchError}</div>}

      {rootFolder && (
        <>
          {selectedFile && (
            <div className="cs-ams-file-card">
              <div className="cs-ams-file-card__left">
                <FiFileText size={16} className="cs-ams-file-card__icon" />
                <div>
                  <p className="cs-ams-file-card__name" title={selectedFile.name}>{selectedFile.name}</p>
                  {selectedFile.size && selectedFile.size !== '-' && (
                    <p className="cs-ams-file-card__size">{fmtSize(selectedFile.size)}</p>
                  )}
                </div>
              </div>
              <button className="cs-ams-file-card__clear"
                onClick={() => { setSelectedFile(null); onFileSelected(null); }}>
                <FiX size={13} />
              </button>
            </div>
          )}

          <div className="cs-ams-breadcrumb">
            <button className="cs-ams-nav-btn" disabled={!canGoBack}    onClick={handleBack}    title="Back">    <FiArrowLeft  size={13} /></button>
            <button className="cs-ams-nav-btn" disabled={!canGoForward} onClick={handleForward} title="Forward"> <FiArrowRight size={13} /></button>
            <div className="cs-ams-breadcrumb__path">
              {pathSegments.map((seg, i) => (
                <React.Fragment key={seg.id}>
                  {i > 0 && <span className="cs-ams-breadcrumb__sep">/</span>}
                  <span className="cs-ams-breadcrumb__seg" title={seg.name}>{seg.name}</span>
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="cs-ams-layout">
            <div className="cs-ams-tree">
              {renderTreeItem(rootFolder)}
            </div>

            <div className="cs-ams-table-wrap">
              {tableLoading ? (
                <div className="cs-ams-state">
                  <span className="cs-ams-spinner" />
                  <span>Loading…</span>
                </div>
              ) : tableData.length === 0 ? (
                <div className="cs-ams-state cs-ams-state--empty">No files in this folder.</div>
              ) : (
                <table className="cs-ams-table">
                  <thead>
                    <tr>
                      <th className="cs-ams-th">Name</th>
                      <th className="cs-ams-th">Type</th>
                      <th className="cs-ams-th">Size</th>
                      <th className="cs-ams-th">Uploader</th>
                      <th className="cs-ams-th">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.map(item => {
                      const isSelected = selectedFile?.id === item.id;
                      return (
                        <tr
                          key={item.id}
                          className={`cs-ams-tr${item.type === 'folder' ? ' cs-ams-tr--folder' : ' cs-ams-tr--file'}${isSelected ? ' cs-ams-tr--active' : ''}`}
                          onClick={() => {
                            if (item.type === 'folder') { toggleFolder(item); return; }
                            setSelectedFile(item);
                            onFileSelected({ file: item, insuredId, folderId: currentFolder?.id || '' });
                          }}
                        >
                          <td className="cs-ams-td cs-ams-td--name">
                            <span title={item.name}>{item.name}</span>
                          </td>
                          <td className="cs-ams-td">{item.description || '—'}</td>
                          <td className="cs-ams-td">{fmtSize(item.size)}</td>
                          <td className="cs-ams-td">{item.uploader || '—'}</td>
                          <td className="cs-ams-td">{item.created || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DriverLicenseScanAmsFiles;

